// useJourneyTracking — instrumenta a jornada do visitante numa página.
//
// Dispara automaticamente:
//   • session_start (1x por sessão) — com referrer bruto e utm params
//   • scroll_depth — quando o usuário cruza cada marco (25/50/75/100%)
//   • section_view — quando cada seção entra na tela pela primeira vez
//   • page_exit — no unload/hide da aba, com tempo total + max scroll +
//     última seção vista. É o evento-âncora para saber ONDE a pessoa parou.
//
// Todos os eventos carregam visitor_id + session_id (via track.js).
import { useEffect, useRef } from "react";
import { track } from "@/lib/track";
import { isFirstEventOfSession, parseUtms } from "@/lib/session";

const SCROLL_MILESTONES = [25, 50, 75, 100];

export const useJourneyTracking = (sectionIds) => {
    // Refs para persistir estado sem forçar re-render
    const startedAt = useRef(Date.now());
    const maxScroll = useRef(0);
    const hitMilestones = useRef(new Set());
    const seenSections = useRef(new Set());
    const lastVisibleSection = useRef(null);
    const exitFired = useRef(false);

    useEffect(() => {
        // 1) session_start — só na primeira chamada por sessão
        if (isFirstEventOfSession()) {
            track("session_start", {
                referrer: (typeof document !== "undefined" && document.referrer) || "",
                ...parseUtms(),
                viewport_w: typeof window !== "undefined" ? window.innerWidth : 0,
                viewport_h: typeof window !== "undefined" ? window.innerHeight : 0,
                lang: typeof navigator !== "undefined" ? navigator.language : "",
            });
        }

        // 2) Scroll depth — marcos 25/50/75/100
        const onScroll = () => {
            const doc = document.documentElement;
            const scrollable = doc.scrollHeight - window.innerHeight;
            if (scrollable <= 0) return;
            const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
            if (pct > maxScroll.current) maxScroll.current = pct;
            for (const m of SCROLL_MILESTONES) {
                if (pct >= m && !hitMilestones.current.has(m)) {
                    hitMilestones.current.add(m);
                    track("scroll_depth", { pct: m });
                }
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });

        // 3) IntersectionObserver — seção visível pela primeira vez
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
                        const id = entry.target.getAttribute("data-section-id");
                        if (!id) continue;
                        lastVisibleSection.current = id;
                        if (!seenSections.current.has(id)) {
                            seenSections.current.add(id);
                            track("section_view", { section: id });
                        }
                    }
                }
            },
            { threshold: [0.3, 0.6] }
        );
        for (const id of sectionIds) {
            const el = document.querySelector(`[data-section-id="${id}"]`);
            if (el) observer.observe(el);
        }

        // 4) page_exit — dispara UMA vez, no visibilitychange (hidden) ou pagehide.
        //    beforeunload é menos confiável em mobile; visibilitychange cobre
        //    troca de aba e fechamento em quase todos os browsers.
        const fireExit = () => {
            if (exitFired.current) return;
            exitFired.current = true;
            const dwell_ms = Date.now() - startedAt.current;
            track("page_exit", {
                dwell_ms,
                dwell_s: Math.round(dwell_ms / 1000),
                max_scroll_pct: maxScroll.current,
                last_section: lastVisibleSection.current || "hero",
                sections_seen: Array.from(seenSections.current),
            });
        };
        const onVis = () => {
            if (document.visibilityState === "hidden") fireExit();
        };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("pagehide", fireExit);

        return () => {
            window.removeEventListener("scroll", onScroll);
            observer.disconnect();
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("pagehide", fireExit);
        };
    // sectionIds é estável em prática (const array literal na página) — se um
    // dia mudar, o cleanup acima cuida da re-inscrição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
